import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatRM, formatDate, DEPARTMENTS, jobPic, DONE_STATUSES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, AgentBadge } from "@/components/StatusBadge";
import { Countdown } from "@/components/Countdown";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ListTodo, Flame, RefreshCw, StickyNote } from "lucide-react";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";

export const ROLE_DEPT = { sales: "sales", customer_service: "sales", finance: "finance", designer: "design", production: "production", qc_packing: "qc" };
const QUEUE_DEPTS = ["sales", "finance", "design", "production", "qc"];

export default function DeptQueue() {
  const { user, hasPerm } = useAuth();
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [dept, setDept] = useState(ROLE_DEPT[user?.role] || "sales");
  const load = () => api.get("/jobs").then((r) => setJobs(r.data));

  useEffect(() => { load(); }, []);
  useAutoRefresh(load, 20000);

  const depts = DEPARTMENTS.filter((d) => QUEUE_DEPTS.includes(d.key));
  const current = depts.find((d) => d.key === dept) || depts[0];
  const inQueue = (d) => (j) => d.statuses.includes(j.status) && !DONE_STATUSES.includes(j.status);
  const queue = jobs
    .filter(inQueue(current))
    .sort((a, b) => (a.priority === "urgent" ? -1 : 1) - (b.priority === "urgent" ? -1 : 1) || (a.deadline || "9").localeCompare(b.deadline || "9"));
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-4" data-testid="dept-queue-page">
      <div>
        <div className="text-xs font-mono text-muted-foreground uppercase">Department Work Board</div>
        <h1 className="text-2xl font-display font-bold flex items-center gap-2"><ListTodo className="w-6 h-6" /> My Queue — {current.label}</h1>
        <p className="text-sm text-muted-foreground">View only. Jobs currently waiting for this department. Update status by scanning the job QR or from the Job Card.</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="dept-tabs">
        {depts.map((d) => (
          <button key={d.key} onClick={() => setDept(d.key)} data-testid={`dept-tab-${d.key}`}
            className={`px-3 py-1.5 rounded-full border text-sm flex items-center gap-2 transition-colors ${dept === d.key ? "bg-cyan-600 text-white border-cyan-600" : "hover:bg-muted"}`}>
            {d.label} <Badge variant={dept === d.key ? "secondary" : "outline"} className="font-mono">{jobs.filter(inQueue(d)).length}</Badge>
          </button>
        ))}
        <Button variant="outline" size="sm" onClick={load} className="ml-auto" data-testid="queue-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {queue.length === 0 && <Card className="p-10 text-center text-muted-foreground" data-testid="queue-empty">No jobs in the {current.label} queue. All clear!</Card>}

      <div className="grid gap-3">
        {queue.map((j) => {
          const overdue = j.deadline && j.deadline < today;
          return (
            <Card key={j.id} className={`p-4 ${overdue ? "border-rose-400" : j.priority === "urgent" ? "border-amber-400" : ""}`} data-testid={`queue-job-${j.job_number}`}>
              <button onClick={() => nav(`/jobs/${j.id}`)} className="text-left w-full">
                <div className="flex items-center gap-2 font-mono font-bold">
                  {j.priority === "urgent" && <Flame className="w-4 h-4 text-orange-500" />}
                  {j.job_number}<AgentBadge job={j} /><StatusBadge status={j.status} />
                </div>
                <div className="text-sm">{j.customer_name} • {j.product} × {j.quantity}</div>
                <div className="text-xs text-muted-foreground">
                  Deadline: <span className={overdue ? "text-rose-600 font-semibold" : ""}>{formatDate(j.deadline)}</span> <Countdown job={j} /> • Last PIC: {jobPic(j)}{hasPerm("sales") && ` • ${formatRM(j.total_price)}`}
                </div>
                {j.handover_note?.text && (
                  <div className="mt-1.5 text-xs flex items-start gap-1.5 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded px-2 py-1" data-testid={`handover-note-${j.job_number}`}>
                    <StickyNote className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                    <span><span className="font-semibold">{j.handover_note.by}:</span> {j.handover_note.text}</span>
                  </div>
                )}
              </button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
